import type { SyntheticEvent } from 'react';
import { memo, useCallback, useEffect, useId, useState } from 'react';
import { Collapse, List } from '@mui/material';
import { NestedDropdownWrapper, SubMenuItem, SubMenuText, SubTextBadgeGroup } from './DropdownMenuItem.styles';
import { MenuItem } from './MenuItem';
import { MenuBadge } from './MenuItem.styles';
import type { IconNames } from '../Icon';
import { buildModulePath, buildAgendaPath } from 'utils/navigation';
import type { Module } from 'types/modules';
import { resolveAgendaName } from 'utils/resolveAgendaName';
import { useBreadcrumbs } from 'contexts/BreadcrumbsProvider';
import { useMenuNavigation } from './useMenuNavigation';
import { toTestIdSegment } from 'utils/testId';
import { buildAgendaNotificationsPath } from 'utils/hooks';
import { isPathActive } from './isPathActive';

interface DropdownMenuItemProps {
  open?: boolean;
  text: string;
  icon?: IconNames;
  links?: {
    path: string;
    name: string;
    unreadNotificationsCount?: number;
  }[];
  path: string;
  openWhenClick?: boolean;
  ownModuleId?: string;
  isNested?: boolean;
  childModules?: Omit<Module, 'childs'>[];
  unreadNotificationsCount?: number;
  dataTestId?: string;
  /** Current pathname — provided by parent to avoid per-item useLocation subscriptions. */
  pathname: string;
  /** Precomputed active state for this item's path. */
  active: boolean;
}

export const DropdownMenuItem = memo(
  ({
    open = false,
    text,
    icon,
    links,
    path,
    openWhenClick,
    ownModuleId,
    isNested = false,
    childModules,
    unreadNotificationsCount,
    dataTestId,
    pathname,
    active,
  }: DropdownMenuItemProps) => {
    const navigateFromMenu = useMenuNavigation();
    const [dropdownOpen, setDropdownOpen] = useState(active);
    const { clearExtras } = useBreadcrumbs();
    const collapseId = useId();

    const toggleDropdownOpen = useCallback(() => {
      setDropdownOpen((prev) => !prev);
    }, []);

    const handleNavigate = useCallback(
      (link: string) => {
        clearExtras();
        navigateFromMenu(link);
      },
      [clearExtras, navigateFromMenu]
    );

    const openAndNavigateHandler = useCallback(() => {
      if (openWhenClick) {
        setDropdownOpen(true);
        handleNavigate(path);
      } else {
        toggleDropdownOpen();
      }
    }, [openWhenClick, handleNavigate, path, toggleDropdownOpen]);

    const subMenuClickHandler = useCallback(
      (event: SyntheticEvent, link: string) => {
        event.stopPropagation();
        (event.currentTarget as HTMLElement).blur();
        handleNavigate(link);
      },
      [handleNavigate]
    );

    // Keep the active section expanded and collapse others when navigating.
    // `pathname` is in deps so the dropdown re-syncs on every navigation
    // (fixes F20-3859 regression where the arrow stayed desynced after
    // collapsing a section and navigating to another agenda within it).
    useEffect(() => {
      setDropdownOpen(active);
    }, [active, pathname]);

    const handleKeyDown = useCallback(
      (event: React.KeyboardEvent) => {
        switch (event.key) {
          case 'ArrowRight':
          case 'ArrowDown':
            if (!dropdownOpen) {
              event.preventDefault();
              setDropdownOpen(true);
            }
            break;
          case 'ArrowLeft':
          case 'ArrowUp':
            if (dropdownOpen) {
              event.preventDefault();
              setDropdownOpen(false);
            }
            break;
          default:
            break;
        }
      },
      [dropdownOpen]
    );

    return (
      <>
        <MenuItem
          active={active}
          open={open}
          dropdownOpen={dropdownOpen}
          icon={icon}
          text={text}
          onClick={openAndNavigateHandler}
          onArrowClick={toggleDropdownOpen}
          onKeyDown={handleKeyDown}
          ariaExpanded={open ? dropdownOpen : undefined}
          ariaControls={open ? collapseId : undefined}
          ariaHasPopup={open ? 'true' : undefined}
          unreadNotificationsCount={unreadNotificationsCount}
          dataTestId={dataTestId ?? `user-menu-item-${toTestIdSegment(text)}`}
          arrow
          nested={isNested}
        />
        {open && (
          <Collapse id={collapseId} in={dropdownOpen} timeout={300}>
            <List component="section" disablePadding>
              {links?.map((link) => {
                const linkActive = isPathActive(pathname, link.path);
                return (
                  <SubMenuItem
                    key={link.path}
                    active={linkActive}
                    nested={isNested}
                    aria-current={linkActive ? 'page' : undefined}
                    onClick={(event) => subMenuClickHandler(event, link.path)}
                    data-testid={`user-menu-subitem-${toTestIdSegment(link.path)}`}>
                    <SubTextBadgeGroup>
                      <SubMenuText active={linkActive}>{link.name}</SubMenuText>
                      {link.unreadNotificationsCount !== undefined && link.unreadNotificationsCount > 0 && (
                        <MenuBadge
                          data-testid={`user-menu-subitem-${toTestIdSegment(link.path)}-badge`}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleNavigate(buildAgendaNotificationsPath(link.path));
                          }}>
                          {link.unreadNotificationsCount}
                        </MenuBadge>
                      )}
                    </SubTextBadgeGroup>
                  </SubMenuItem>
                );
              })}
              {childModules &&
                childModules.length > 0 &&
                childModules.map(
                  (childModule) =>
                    childModule.agendas.length > 0 && (
                      <NestedDropdownWrapper key={childModule.moduleId}>
                        <DropdownMenuItem
                          text={childModule.name}
                          open={open}
                          icon={childModule.icon as IconNames}
                          path={buildModulePath(childModule.moduleId, ownModuleId)}
                          dataTestId={`sidebar-user-module-${toTestIdSegment(childModule.moduleId)}`}
                          openWhenClick
                          ownModuleId={ownModuleId}
                          isNested
                          unreadNotificationsCount={childModule.unreadNotificationsCount}
                          pathname={pathname}
                          active={isPathActive(pathname, buildModulePath(childModule.moduleId, ownModuleId))}
                          links={childModule.agendas
                            .filter((agenda) => !agenda.hideInOverview)
                            .map((agenda) => ({
                              name: resolveAgendaName(agenda),
                              path: buildAgendaPath(childModule.moduleId, agenda, ownModuleId),
                              unreadNotificationsCount: agenda.unreadNotificationsCount,
                            }))}
                        />
                      </NestedDropdownWrapper>
                    )
                )}
            </List>
          </Collapse>
        )}
      </>
    );
  }
);
